/**
 * Wizard validation context.
 *
 * Centralises the "submit attempted" UX pattern for the registration
 * wizard so every step shares the same behaviour:
 *
 *   1. Continue button is **always enabled**.
 *   2. Steps register synchronous `() => boolean` validators while
 *      mounted. useForm-based cards use `useRegisterFormValidator`
 *      which submits the form on first click, then checks errors
 *      on subsequent clicks.
 *   3. Clicking Continue runs every registered validator. If any
 *      returns false, `attempted` flips to true and components use it
 *      to show inline per-field errors (red border + bottom hint).
 *      Otherwise `next()` runs and `attempted` resets.
 *
 * The provider has no internal "reset on step change" effect - instead
 * callers should pass a `key` prop tied to the active step so React
 * unmounts and remounts the provider, which naturally clears both
 * `attempted` and the registered validator map.
 */
/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { FormContext } from '@/shared/lib/forms'

type Validator = () => boolean | Promise<boolean>

interface WizardValidationContextValue {
  /**
   * True after a Continue click for the current step found unmet
   * required fields. Components read this to render inline error states.
   */
  attempted: boolean
  /**
   * Register a validator (sync or async). Returns an unregister
   * callback; callers should run it on unmount.
   */
  registerValidator: (id: string, validator: Validator) => () => void
  /**
   * Wraps the step's `next()` callback. Runs every registered
   * validator; advances only when all return true.
   */
  attemptContinue: (next: () => void | Promise<void>) => void
}

const noopUnregister = () => {}

const defaultValue: WizardValidationContextValue = {
  attempted: false,
  registerValidator: () => noopUnregister,
  attemptContinue: (next) => {
    void next()
  },
}

const WizardValidationContext =
  createContext<WizardValidationContextValue>(defaultValue)

interface ProviderProps {
  children: React.ReactNode
}

export function WizardValidationProvider({ children }: ProviderProps) {
  const [attempted, setAttempted] = useState(false)
  const validators = useRef<Map<string, Validator>>(new Map())

  const registerValidator = useCallback(
    (id: string, fn: Validator) => {
      validators.current.set(id, fn)
      return () => {
        validators.current.delete(id)
      }
    },
    [],
  )

  const attemptContinue = useCallback(
    async (next: () => void | Promise<void>) => {
      let allValid = true
      for (const fn of validators.current.values()) {
        const result = fn()
        const valid = result instanceof Promise ? await result : result
        if (!valid) allValid = false
      }
      if (!allValid) {
        setAttempted(true)
        return
      }
      setAttempted(false)
      void next()
    },
    [],
  )

  const value = useMemo<WizardValidationContextValue>(
    () => ({ attempted, registerValidator, attemptContinue }),
    [attempted, registerValidator, attemptContinue],
  )

  return (
    <WizardValidationContext.Provider value={value}>
      {children}
    </WizardValidationContext.Provider>
  )
}

export function useWizardValidation() {
  return useContext(WizardValidationContext)
}

/**
 * Register a boolean validator with the wizard validation context.
 * The latest `isValid` value is captured via ref so the registered
 * function always sees the current state without re-registering on
 * every render. The ref is mutated inside an effect (not during
 * render) to satisfy React's rules-of-refs lint.
 */
export function useRegisterValidator(id: string, isValid: boolean) {
  const { registerValidator } = useWizardValidation()
  const ref = useRef(isValid)

  useEffect(() => {
    ref.current = isValid
  }, [isValid])

  useEffect(() => {
    return registerValidator(id, () => ref.current)
  }, [id, registerValidator])
}

/**
 * Register a generated form with the wizard validation context.
 *
 * The form's `useForm` submit handler is the single source of truth for
 * "this form is valid": React Hook Form only calls it when validation
 * passes. That handler must set `collectedRef.current = true` (and
 * collect its values) - see `DetailFieldsForm`.
 *
 *  - On Continue, the wizard validator calls `form.submitForm()`.
 *  - If the form is invalid, RHF renders inline field errors and never
 *    calls the submit handler → `collectedRef` stays false → the
 *    validator returns false and the wizard stays on the step.
 *  - If the form is valid, the submit handler runs (setting
 *    `collectedRef`) → the validator returns true.
 */
export function useRegisterFormValidator(
  id: string,
  formRef: React.RefObject<FormContext | null>,
  collectedRef: React.MutableRefObject<boolean>,
) {
  const { registerValidator } = useWizardValidation()

  useEffect(() => {
    return registerValidator(id, async () => {
      const form = formRef.current
      if (!form) return true

      collectedRef.current = false
      await form.submitForm()
      return collectedRef.current
    })
  }, [id, registerValidator, formRef, collectedRef])
}

/**
 * Spec describing a single required field for the wizard validation
 * context. `isFilled` tells the helper whether the field currently
 * passes the "required" check.
 */
export interface RequiredFieldSpec {
  key: string
  label: string
  isFilled: boolean
  /** Optional override; defaults to `${label} is required`. */
  message?: string
}

/**
 * Convenience hook that registers a single validator covering a list
 * of required field specs and returns the per-field error map.
 *
 * Errors are only populated once the user has attempted Continue;
 * before that the map is empty so untouched fields stay clean.
 */
export function useRequiredFieldErrors(
  scopeId: string,
  specs: RequiredFieldSpec[],
): Record<string, string> {
  const { attempted } = useWizardValidation()
  const allValid = specs.every((s) => s.isFilled)
  useRegisterValidator(scopeId, allValid)

  return useMemo<Record<string, string>>(() => {
    if (!attempted) return {}
    const out: Record<string, string> = {}
    for (const spec of specs) {
      if (!spec.isFilled) {
        out[spec.key] = spec.message ?? `${spec.label} is required`
      }
    }
    return out
    // The spec list changes identity per render but its content is what
    // matters; rely on `attempted` flips + caller-side memoisation.
  }, [attempted, specs])
}

/**
 * Common helper: a string value counts as filled when it has at least
 * one non-whitespace character. Other primitives are considered filled
 * when they're not null/undefined; arrays are filled when non-empty.
 */
export function isValueFilled(value: unknown): boolean {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'number') return !Number.isNaN(value)
  return true
}
